import type {
  Account,
  ContributionRule,
  EventItem,
  FundingScenario,
  LargePurchase,
  Scenario,
  ScenarioMeta,
  Snapshot,
} from '../types';
import type { SeedData } from './seed';

/**
 * A deliberately fake profile used for demos and screenshots. Different
 * person, different numbers, different accounts — nothing here is tied to
 * the user's real data. Covers every feature the app supports so screenshots
 * feel lived-in: many accounts, ~14 months of snapshots with realistic noise,
 * a Baseline scenario with near-term purchases, and branch scenarios that
 * exercise the full event taxonomy (home swap, sabbatical, early retirement).
 */
export function buildDemoSeed(): SeedData {
  const now = new Date().toISOString();

  const accounts: Account[] = [
    mk('d-hysa', 'Ally HYSA', 'hysa', 'hysa', 0),
    mk('d-checking', 'Chase Checking', 'checking', null, 1),
    mk('d-401k', 'Empower 401(k)', 'retirement', 'retirement', 2),
    mk('d-rothira', 'Roth IRA', 'retirement', 'retirement', 3),
    mk('d-vanguard', 'Vanguard Brokerage', 'taxable', 'taxable', 4),
    mk('d-schwab', 'Schwab Individual', 'taxable', 'taxable', 5),
    mk('d-home', 'Primary Residence', 'home', null, 6),
    mk('d-mortgage', 'Primary Residence Mortgage', 'mortgage', null, 7),
    mk('d-car-loan', 'Auto Loan', 'other_liability', null, 8),
  ];

  const snapshots = buildSnapshotHistory();

  const assumptions = {
    id: 'singleton' as const,
    currentAge: 42,
    targetRetirementAge: 55,
    startingCalendarYear: 2026,
    // Legacy fields kept populated; engine prefers `compensation` and
    // `contributionRules` below.
    annualContribution401k: 30_000,
    annualContributionTaxable: 145_000,
    annualContributionHysa: 0,
    grossAnnualSalary: 350_000,
    compensation: {
      baseAnnual: 280_000,
      bonusAnnual: 70_000,
      bonusMonth: 2 as const,
      employer401kMatchPct: 0.05,
      employer401kMatchCapPct: 0.06,
    },
    contributionRules: [
      {
        id: 'demo-401k',
        name: '401(k) employee pretax',
        target: { kind: 'bucket', bucket: 'retirement' },
        mode: { kind: 'fixed_dollar', annual: 30_000 },
        schedule: { kind: 'lump_sum', month: 1 },
        taxTreatment: 'pretax',
        enabled: true,
        order: 0,
      },
      {
        id: 'demo-vanguard',
        name: 'Vanguard auto-invest',
        target: { kind: 'account', accountId: 'd-vanguard' },
        mode: { kind: 'fixed_dollar', annual: 100_000 },
        schedule: { kind: 'monthly' },
        taxTreatment: 'after_tax_brokerage',
        enabled: true,
        order: 1,
      },
      {
        id: 'demo-schwab',
        name: 'Schwab DCA',
        target: { kind: 'account', accountId: 'd-schwab' },
        mode: { kind: 'fixed_dollar', annual: 45_000 },
        schedule: { kind: 'monthly' },
        taxTreatment: 'after_tax_brokerage',
        enabled: true,
        order: 2,
      },
    ] satisfies ContributionRule[],
    stockRealReturn: 0.06,
    hysaRealReturn: 0.01,
    homeRealAppreciation: 0.005,
    swr: 0.035,
    inflation: 0.03,
    mortgageAnnualRate: 0.0575,
    mortgageMonthlyPI: 3_520,
    mortgageMonthlyEscrow: 1_040,
    monthlyHouseholdSpend: 16_500,
    healthcareAnnual: 28_000,
    filingStatus: 'married' as const,
    ltcgRate: 0.15,
    ordinaryRate: 0.32,
    stateTaxRate: 0.05,
    earlyWithdrawalPenalty: 0.1,
    homeCostBasis: 760_000,
    updatedAt: now,
  };

  const purchases = buildPurchases(now);
  const scenarios = buildScenarios(now);

  const meta: ScenarioMeta = {
    id: 'singleton',
    activeScenarioId: 'baseline',
    compareScenarioIds: ['upgrade-home', 'retire-early'],
    updatedAt: now,
  };

  return { accounts, snapshots, assumptions, purchases, scenarios, meta };
}

// ── Snapshot history ────────────────────────────────────────────────────────

function buildSnapshotHistory(): Snapshot[] {
  const dates: string[] = [
    '2025-02-14', '2025-03-01', '2025-03-17', '2025-04-02',
    '2025-04-19', '2025-05-07', '2025-05-22', '2025-06-05',
    '2025-06-20', '2025-07-03', '2025-07-18', '2025-08-01',
    '2025-08-16', '2025-09-02', '2025-09-18', '2025-10-04',
    '2025-10-19', '2025-11-01', '2025-11-16', '2025-12-03',
    '2025-12-20', '2026-01-05', '2026-01-19', '2026-02-03',
    '2026-02-17', '2026-03-04', '2026-03-20', '2026-04-03',
    '2026-04-17', '2026-04-20',
  ];

  let seed = 424242;
  const rnd = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 2 ** 32;
  };
  const noise = (mag: number) => (rnd() - 0.5) * 2 * mag;

  let hysa = 162_400;
  let checking = 27_800;
  let k401 = 812_300;
  let roth = 228_100;
  let vanguard = 548_700;
  let schwab = 402_900;
  let home = 1_145_000;
  let mortgage = 548_200;
  let car = 38_900;

  const rows: Snapshot[] = [];
  for (let i = 0; i < dates.length; i++) {
    const date = dates[i];
    const isPayday = i % 2 === 0;
    const isEndOfYear = date.startsWith('2025-12') || date.startsWith('2026-01');

    hysa += isPayday ? 2_400 + noise(700) : noise(1_100);
    checking += isPayday ? 4_800 + noise(1_400) : -3_200 + noise(900);
    if (checking < 8_000) checking = 8_000 + noise(800);
    k401 += 6_100 + noise(4_200);
    roth += 320 + noise(1_100);
    vanguard += 3_800 + noise(9_200);
    schwab += 2_900 + noise(6_400);
    home += i % 4 === 0 ? 2_100 + noise(2_400) : 0;
    mortgage -= 620 + noise(30);
    car -= 610 + noise(15);
    if (car < 0) car = 0;

    if (isEndOfYear) {
      vanguard += noise(12_000);
      schwab += noise(9_000);
      k401 += noise(14_000);
    }

    const skipHome = i % 7 === 3;
    const skipCar = i % 5 === 2;
    const skipSchwab = i === 11 || i === 22;

    const balances: Record<string, number> = {
      'd-hysa': round(hysa),
      'd-checking': round(checking),
      'd-401k': round(k401),
      'd-rothira': round(roth),
      'd-vanguard': round(vanguard),
      'd-mortgage': round(mortgage),
    };
    if (!skipHome) balances['d-home'] = round(home);
    if (!skipCar) balances['d-car-loan'] = round(car);
    if (!skipSchwab) balances['d-schwab'] = round(schwab);

    rows.push({
      id: `demo-snap-${i}`,
      date,
      balances,
      notes:
        i === 0 ? 'Started tracking'
        : i === 7 ? 'Q1 bonus hit'
        : i === 14 ? 'Summer roof repairs'
        : i === 20 ? 'Year-end rebalance'
        : i === 28 ? 'Tax refund → HYSA'
        : undefined,
      createdAt: `${date}T12:00:00.000Z`,
    });
  }
  return rows;
}

function round(n: number): number {
  return Math.max(0, Math.round(n));
}

// ── Legacy purchases (kept so v1 exports still import cleanly) ──────────────

function buildPurchases(now: string): LargePurchase[] {
  const macan: FundingScenario[] = [
    {
      id: 'macan-hysa',
      name: 'Pay cash (HYSA)',
      sources: [{ id: 'm-1', type: 'hysa', amount: 95_000 }],
    },
  ];
  const kitchen: FundingScenario[] = [
    {
      id: 'kit-cash',
      name: 'From savings',
      sources: [{ id: 'k-1', type: 'cash_flow', amount: 95_000 }],
    },
  ];
  const tuition: FundingScenario[] = [
    {
      id: 'tuit-cashflow',
      name: '529 + cash flow',
      sources: [{ id: 't-2', type: 'cash_flow', amount: 110_000 }],
    },
  ];
  return [
    { id: 'demo-macan', name: 'New truck', yearOffset: 1, amount: 95_000, archived: false, enabled: true, createdAt: now, scenarios: macan, activeScenarioId: 'macan-hysa' },
    { id: 'demo-kitchen', name: 'Kitchen renovation', yearOffset: 2, amount: 95_000, archived: false, enabled: true, createdAt: now, scenarios: kitchen, activeScenarioId: 'kit-cash' },
    { id: 'demo-tuition', name: 'College tuition', yearOffset: 4, amount: 110_000, archived: false, enabled: true, createdAt: now, scenarios: tuition, activeScenarioId: 'tuit-cashflow' },
  ];
}

// ── Scenarios (the new v2 model) ────────────────────────────────────────────
//
// Six scenarios that together show off the feature surface:
//   • Baseline        — daily life (truck, kitchen, tuition)
//   • Upgrade home    — Baseline + bigger house in 6 years via home_buy
//   • Retire early    — Baseline + retire at 50 instead of 55
//   • Sabbatical      — Baseline + 1-year career break at 45
//   • Coast FIRE      — stop contributing entirely at 48, keep working
//   • Fat FIRE        — Baseline + big windfall + boat + extra spend

function buildScenarios(now: string): Scenario[] {
  const baseEvents: EventItem[] = [
    {
      id: 'ev-truck',
      kind: 'purchase',
      yearOffset: 1,
      name: 'New truck',
      amount: 95_000,
      enabled: true,
      createdAt: now,
      funding: {
        id: 'fs-truck',
        name: 'Cash',
        sources: [{ id: 'src-truck', type: 'hysa', amount: 95_000 }],
      },
    },
    {
      id: 'ev-kitchen',
      kind: 'purchase',
      yearOffset: 2,
      name: 'Kitchen renovation',
      amount: 95_000,
      enabled: true,
      createdAt: now,
      funding: {
        id: 'fs-kitchen',
        name: 'From savings',
        sources: [{ id: 'src-kitchen', type: 'cash_flow', amount: 95_000 }],
      },
    },
    {
      id: 'ev-tuition',
      kind: 'purchase',
      yearOffset: 4,
      name: 'College tuition',
      amount: 110_000,
      enabled: true,
      createdAt: now,
      funding: {
        id: 'fs-tuition',
        name: '529 + cash flow',
        sources: [{ id: 'src-tuition', type: 'cash_flow', amount: 110_000 }],
      },
    },
  ];

  const baseline: Scenario = {
    id: 'baseline',
    name: 'Baseline',
    description: 'Current plan of record',
    icon: '🗻',
    color: '#3b54e8',
    order: 0,
    archived: false,
    events: baseEvents,
    createdAt: now,
  };

  const upgradeHome: Scenario = {
    id: 'upgrade-home',
    name: 'Upgrade home',
    description: 'Sell current home in year 6 and buy a $1.95M place',
    icon: '🏡',
    color: '#059669',
    order: 1,
    archived: false,
    events: [
      ...baseEvents,
      {
        id: 'ev-home-swap',
        kind: 'home_buy',
        yearOffset: 6,
        name: 'Bigger house',
        enabled: true,
        createdAt: now,
        purchasePrice: 1_950_000,
        downPayment: 1_050_000,
        mortgagePrincipal: 900_000,
        mortgageAnnualRate: 0.065,
        mortgageTermYears: 30,
        sellCurrentHome: true,
        newCostBasis: 1_950_000,
        downPaymentFunding: {
          id: 'fs-home-dp',
          name: 'Current home sale',
          sources: [
            {
              id: 'src-sale',
              type: 'home_sale',
              salePrice: 1_450_000,
              useSection121: true,
            },
          ],
        },
      },
    ],
    createdAt: now,
  };

  const retireEarly: Scenario = {
    id: 'retire-early',
    name: 'Retire at 50',
    description: 'Shift retirement 5 years earlier',
    icon: '🌴',
    color: '#f59e0b',
    order: 2,
    archived: false,
    events: [
      ...baseEvents,
      {
        id: 'ev-retire-50',
        kind: 'retire',
        yearOffset: 0,
        name: 'Target age 50',
        enabled: true,
        createdAt: now,
        retirementAge: 50,
      },
    ],
    createdAt: now,
  };

  const sabbatical: Scenario = {
    id: 'sabbatical',
    name: 'Sabbatical at 45',
    description: '1 year off, no income, live off HYSA',
    icon: '🏖️',
    color: '#0891b2',
    order: 3,
    archived: false,
    events: [
      ...baseEvents,
      {
        id: 'ev-gap',
        kind: 'job_gap',
        yearOffset: 3,
        name: 'Career break',
        enabled: true,
        createdAt: now,
        durationYears: 1,
      },
    ],
    createdAt: now,
  };

  const coastFire: Scenario = {
    id: 'coast-fire',
    name: 'Coast FIRE',
    description: 'Stop contributing at 48; keep working for current spend',
    icon: '🛟',
    color: '#8b5cf6',
    order: 4,
    archived: false,
    events: [
      ...baseEvents,
      {
        id: 'ev-coast',
        kind: 'contribution_change',
        yearOffset: 6,
        name: 'Stop all contributions',
        enabled: true,
        createdAt: now,
        newRetirement: 0,
        newTaxable: 0,
        newHysa: 0,
      },
    ],
    createdAt: now,
  };

  const fatFire: Scenario = {
    id: 'fat-fire',
    name: 'Fat FIRE',
    description: 'Big inheritance at 47 plus the boat',
    icon: '🛥️',
    color: '#dc2626',
    order: 5,
    archived: false,
    events: [
      ...baseEvents,
      {
        id: 'ev-inheritance',
        kind: 'windfall',
        yearOffset: 5,
        name: 'Inheritance',
        enabled: true,
        createdAt: now,
        amount: 750_000,
        toBucket: 'taxable',
        taxFree: true,
      },
      {
        id: 'ev-boat',
        kind: 'purchase',
        yearOffset: 8,
        name: 'Boat',
        amount: 185_000,
        enabled: true,
        createdAt: now,
        funding: {
          id: 'fs-boat',
          name: 'HYSA',
          sources: [{ id: 'src-boat', type: 'hysa', amount: 185_000 }],
        },
      },
    ],
    createdAt: now,
  };

  return [baseline, upgradeHome, retireEarly, sabbatical, coastFire, fatFire];
}

function mk(
  id: string,
  name: string,
  kind: Account['kind'],
  bucket: Account['bucket'],
  order: number,
): Account {
  return {
    id,
    name,
    kind,
    bucket,
    realReturnOverride: null,
    order,
    archived: false,
    createdAt: '2025-02-14T00:00:00.000Z',
  };
}
